import React, { createContext, useContext, useState, useCallback } from "react";

const dict = {
  en: {
    nav: { home: "Home", brands: "For Brands", publishers: "For Publishers", solutions: "Solutions",
      how: "How It Works", about: "About", insights: "Insights", contact: "Contact" },
    cta: { work: "Work With WANM", publisher: "Become a Publisher", explore: "Explore Solutions",
      join: "Join WANM", talk: "Talk to WANM", launch: "Launch a Campaign", apply: "Apply as a Publisher" },
    tagline: "Performance Marketing & Affiliate Network",
    home: {
      heroLines: ["Where Brands", "Meet Performance."],
      heroSub: "WANM connects brands and publishers through performance-driven partnerships designed to generate measurable growth.",
      scroll: "Scroll to explore",
      ecoTitle: "The performance ecosystem",
      ecoSub: "WANM sits at the center of the value exchange — aligning advertiser objectives with publisher incentives.",
      nodes: ["Brands", "WANM", "Publishers", "Consumers", "Conversions"],
      trustOver: "A Performance-Driven Partner",
      trustTitle: "We connect brands with performance.",
      trustBody: "WANM helps advertisers reach qualified audiences through trusted publisher relationships, while giving publishers access to quality offers and dependable monetization. Every partnership is measured, optimized and built to scale.",
      twoOver: "A two-sided marketplace",
      brandCardTitle: "For Brands",
      brandCardBody: "Scale customer acquisition through performance-based partnerships.",
      pubCardTitle: "For Publishers",
      pubCardBody: "Access quality offers and turn traffic into measurable revenue.",
      solOver: "What we do",
      solTitle: "Solutions built around outcomes.",
      howOver: "How it works",
      howTitle: "From brief to scaled performance.",
      perfOver: "Measurement",
      perfTitle: "Performance you can measure.",
      perfBody: "Transparent tracking, clean attribution and continuous optimization — so every decision is grounded in real conversion data.",
      finalTitle: "Ready to build performance-driven growth?",
    },
    solutions: [
      { t: "Affiliate Marketing", d: "Connecting brands and publishers through measurable partnerships." },
      { t: "Performance Marketing", d: "Acquisition focused on measurable business outcomes." },
      { t: "Publisher Partnerships", d: "Building and managing high-quality publisher relationships." },
      { t: "Campaign Management", d: "Structuring, launching and optimizing performance campaigns." },
      { t: "Tracking & Attribution", d: "Measurement infrastructure for conversions and performance." },
      { t: "Customer Acquisition", d: "Helping brands scale qualified customer acquisition through partner ecosystems." },
    ],
    perfPoints: ["Tracking", "Attribution", "Conversion data", "Campaign optimization", "Publisher performance", "Transparent reporting"],
    steps: [
      { n: "01", t: "Brand joins WANM", d: "We understand the brand, its goals and its audience." },
      { n: "02", t: "Campaign is structured", d: "We design the offer, payout model and success metrics." },
      { n: "03", t: "Publishers are activated", d: "Qualified publishers are matched to the campaign." },
      { n: "04", t: "Conversions are tracked", d: "Traffic and conversions are measured transparently." },
      { n: "05", t: "Performance is scaled", d: "We optimize continuously and scale what works." },
    ],
    brands: {
      heroOver: "For Brands",
      heroTitle: "Turn Performance Into Growth.",
      heroSub: "WANM gives brands direct access to a vetted publisher ecosystem and a performance-based acquisition model — you pay for outcomes, not promises.",
      whyOver: "Why WANM",
      whyTitle: "Acquisition, engineered around results.",
      why: ["Access to publisher partnerships", "Performance-based acquisition", "Campaign management",
        "Tracking and attribution", "Continuous optimization", "Scalable acquisition"],
      procOver: "Our process",
      formOver: "Launch a campaign",
      formTitle: "Tell us about your growth goals.",
    },
    publishers: {
      heroOver: "For Publishers",
      heroTitle: "Monetize Your Traffic With Performance.",
      heroSub: "WANM connects publishers with relevant, high-quality offers and reliable tracking — so your qualified traffic becomes measurable revenue.",
      whyOver: "Why join WANM",
      whyTitle: "Built for publishers who take performance seriously.",
      why: ["Quality offers", "Competitive opportunities", "Reliable tracking", "Transparent performance",
        "Dedicated communication", "Multiple verticals", "Growth opportunities"],
      formOver: "Apply as a publisher",
      formTitle: "Join the WANM network.",
      disclaimer: "We do not guarantee earnings or approval. Every application is reviewed individually.",
    },
    solutionsPage: {
      heroOver: "Solutions",
      heroTitle: "A complete performance stack.",
      heroSub: "Six connected capabilities that move a brand from campaign brief to scaled, measurable acquisition.",
    },
    howPage: {
      heroOver: "How it works",
      heroTitle: "One ecosystem. Aligned incentives.",
      heroSub: "WANM creates alignment between advertiser objectives and publisher incentives — every stage feeds data back into the next.",
      flow: ["Advertiser", "WANM", "Publisher", "Audience", "Conversion", "Data", "Optimization"],
      flowDesc: ["Defines the objective and the offer.", "Structures the campaign and matches partners.",
        "Activates qualified traffic sources.", "Reaches relevant, qualified consumers.",
        "Turns intent into measurable action.", "Captures transparent performance signals.",
        "Feeds insight back to improve results."],
    },
    about: {
      heroOver: "About WANM",
      heroTitle: "We Are Nation Media.",
      heroSub: "A performance-driven company operating at the intersection of brands, media and measurable growth.",
      chapters: [
        { n: "01", t: "Mission", d: "To connect brands and publishers through partnerships that are measurable, transparent and built to scale." },
        { n: "02", t: "Vision", d: "A performance marketing ecosystem where advertiser objectives and publisher incentives are always aligned." },
        { n: "03", t: "Values", d: "Transparency, measurable outcomes, and long-term partnership over short-term wins." },
        { n: "04", t: "How we work", d: "We structure every campaign around clear success metrics, then optimize continuously against real conversion data." },
        { n: "05", t: "Why WANM exists", d: "Because growth should be earned through performance — not promised through vanity metrics." },
      ],
    },
    insights: {
      heroOver: "Insights",
      heroTitle: "Perspectives on performance.",
      heroSub: "Ideas on affiliate marketing, performance strategy and the business of measurable growth.",
      read: "Read article",
      empty: "No articles in this category yet.",
      back: "All Insights",
    },
    contact: {
      heroOver: "Contact",
      heroTitle: "Let's talk performance.",
      brandPath: "I am a Brand",
      brandPathBody: "Scale acquisition through our publisher ecosystem.",
      pubPath: "I am a Publisher",
      pubPathBody: "Monetize your traffic with quality offers.",
      formOver: "General inquiry",
      formTitle: "Send us a message.",
    },
    form: {
      name: "Full name", company: "Company", email: "Work email", website: "Website",
      industry: "Industry", budget: "Monthly marketing budget", objective: "Main acquisition objective",
      message: "Message", country: "Country", traffic_sources: "Traffic sources",
      verticals: "Main verticals", monthly_traffic: "Monthly traffic", networks: "Current affiliate networks",
      submit: "Submit", sending: "Sending…", success: "Thank you — we'll be in touch shortly.",
      error: "Something went wrong. Please try again.", required: "This field is required.",
    },
    footer: {
      desc: "WANM connects brands and publishers through performance-driven partnerships. Performance Marketing & Affiliate Network.",
      nav: "Navigate", legal: "Legal", connect: "Connect",
      privacy: "Privacy Policy", terms: "Terms & Conditions", cookie: "Cookie Policy",
      rights: "All rights reserved.", big: "Let's build performance.",
      placeholder: "Company details coming soon",
      cookiePreferences: "Cookie Preferences", comingSoon: "Coming soon",
      contact: "Contact",
    },
    cookies: {
      title: "Your privacy, your choice",
      body: "WANM uses cookies and similar technologies for essential website functionality and, with your consent, for analytics and future marketing purposes. You can accept all, reject non-essential cookies, or choose what to allow.",
      acceptAll: "Accept All", rejectAll: "Reject All", manage: "Manage Preferences",
      save: "Save Preferences", cancel: "Cancel",
      prefTitle: "Cookie Preferences",
      prefIntro: "Manage how WANM uses cookies. Strictly necessary cookies are always on. You can change these choices at any time from the footer.",
      always: "Always on", policyLink: "Read our Cookie Policy",
      privacyLink: "Privacy Policy",
    },
    admin: { title: "WANM Admin", login: "Sign in", leads: "Leads", articles: "Articles",
      logout: "Log out", newArticle: "New Article", noLeads: "No submissions yet." },
  },
  es: {
    nav: { home: "Inicio", brands: "Para Marcas", publishers: "Para Publishers", solutions: "Soluciones",
      how: "Cómo Funciona", about: "Nosotros", insights: "Ideas", contact: "Contacto" },
    cta: { work: "Trabaja Con WANM", publisher: "Conviértete en Publisher", explore: "Explorar Soluciones",
      join: "Únete a WANM", talk: "Habla Con WANM", launch: "Lanzar Campaña", apply: "Postúlate Como Publisher" },
    tagline: "Marketing de Resultados & Red de Afiliación",
    home: {
      heroLines: ["Donde las Marcas", "Encuentran Resultados."],
      heroSub: "WANM conecta marcas y publishers mediante alianzas basadas en resultados, diseñadas para generar crecimiento medible.",
      scroll: "Desplázate para explorar",
      ecoTitle: "El ecosistema de resultados",
      ecoSub: "WANM está en el centro del intercambio de valor, alineando los objetivos del anunciante con los incentivos del publisher.",
      nodes: ["Marcas", "WANM", "Publishers", "Consumidores", "Conversiones"],
      trustOver: "Un Socio Orientado a Resultados",
      trustTitle: "Conectamos marcas con resultados.",
      trustBody: "WANM ayuda a los anunciantes a llegar a audiencias cualificadas mediante relaciones de confianza con publishers, y ofrece a los publishers acceso a ofertas de calidad y una monetización fiable. Cada alianza se mide, se optimiza y se construye para escalar.",
      twoOver: "Un mercado de dos lados",
      brandCardTitle: "Para Marcas",
      brandCardBody: "Escala la adquisición de clientes con alianzas basadas en resultados.",
      pubCardTitle: "Para Publishers",
      pubCardBody: "Accede a ofertas de calidad y convierte tu tráfico en ingresos medibles.",
      solOver: "Qué hacemos",
      solTitle: "Soluciones construidas en torno a resultados.",
      howOver: "Cómo funciona",
      howTitle: "Del brief al rendimiento escalado.",
      perfOver: "Medición",
      perfTitle: "Rendimiento que puedes medir.",
      perfBody: "Seguimiento transparente, atribución limpia y optimización continua, para que cada decisión se base en datos de conversión reales.",
      finalTitle: "¿Listo para construir crecimiento basado en resultados?",
    },
    solutions: [
      { t: "Marketing de Afiliación", d: "Conectamos marcas y publishers mediante alianzas medibles." },
      { t: "Marketing de Resultados", d: "Adquisición enfocada en resultados de negocio medibles." },
      { t: "Alianzas con Publishers", d: "Creamos y gestionamos relaciones de alta calidad con publishers." },
      { t: "Gestión de Campañas", d: "Estructuramos, lanzamos y optimizamos campañas de resultados." },
      { t: "Tracking y Atribución", d: "Infraestructura de medición para conversiones y rendimiento." },
      { t: "Adquisición de Clientes", d: "Ayudamos a las marcas a escalar la adquisición cualificada mediante ecosistemas de socios." },
    ],
    perfPoints: ["Tracking", "Atribución", "Datos de conversión", "Optimización de campañas", "Rendimiento de publishers", "Reportes transparentes"],
    steps: [
      { n: "01", t: "La marca se une a WANM", d: "Entendemos la marca, sus objetivos y su audiencia." },
      { n: "02", t: "Se estructura la campaña", d: "Diseñamos la oferta, el modelo de pago y las métricas de éxito." },
      { n: "03", t: "Se activan los publishers", d: "Publishers cualificados se asignan a la campaña." },
      { n: "04", t: "Se miden las conversiones", d: "El tráfico y las conversiones se miden con transparencia." },
      { n: "05", t: "Se escala el rendimiento", d: "Optimizamos continuamente y escalamos lo que funciona." },
    ],
    brands: {
      heroOver: "Para Marcas",
      heroTitle: "Convierte el Rendimiento en Crecimiento.",
      heroSub: "WANM da a las marcas acceso directo a un ecosistema de publishers verificados y un modelo de adquisición basado en resultados: pagas por resultados, no por promesas.",
      whyOver: "Por qué WANM",
      whyTitle: "Adquisición diseñada en torno a resultados.",
      why: ["Acceso a alianzas con publishers", "Adquisición basada en resultados", "Gestión de campañas",
        "Tracking y atribución", "Optimización continua", "Adquisición escalable"],
      procOver: "Nuestro proceso",
      formOver: "Lanzar una campaña",
      formTitle: "Cuéntanos tus objetivos de crecimiento.",
    },
    publishers: {
      heroOver: "Para Publishers",
      heroTitle: "Monetiza Tu Tráfico Con Resultados.",
      heroSub: "WANM conecta a los publishers con ofertas relevantes y de alta calidad y con un tracking fiable, para que tu tráfico cualificado se convierta en ingresos medibles.",
      whyOver: "Por qué unirte a WANM",
      whyTitle: "Hecho para publishers que se toman en serio el rendimiento.",
      why: ["Ofertas de calidad", "Oportunidades competitivas", "Tracking fiable", "Rendimiento transparente",
        "Comunicación dedicada", "Múltiples verticales", "Oportunidades de crecimiento"],
      formOver: "Postúlate como publisher",
      formTitle: "Únete a la red WANM.",
      disclaimer: "No garantizamos ganancias ni aprobación. Cada solicitud se revisa de forma individual.",
    },
    solutionsPage: {
      heroOver: "Soluciones",
      heroTitle: "Un stack de resultados completo.",
      heroSub: "Seis capacidades conectadas que llevan a una marca del brief de campaña a una adquisición escalada y medible.",
    },
    howPage: {
      heroOver: "Cómo funciona",
      heroTitle: "Un ecosistema. Incentivos alineados.",
      heroSub: "WANM crea alineación entre los objetivos del anunciante y los incentivos del publisher: cada etapa devuelve datos a la siguiente.",
      flow: ["Anunciante", "WANM", "Publisher", "Audiencia", "Conversión", "Datos", "Optimización"],
      flowDesc: ["Define el objetivo y la oferta.", "Estructura la campaña y asigna socios.",
        "Activa fuentes de tráfico cualificado.", "Llega a consumidores relevantes y cualificados.",
        "Convierte la intención en acción medible.", "Captura señales de rendimiento transparentes.",
        "Devuelve insights para mejorar resultados."],
    },
    about: {
      heroOver: "Nosotros",
      heroTitle: "We Are Nation Media.",
      heroSub: "Una empresa orientada a resultados que opera en la intersección de marcas, medios y crecimiento medible.",
      chapters: [
        { n: "01", t: "Misión", d: "Conectar marcas y publishers mediante alianzas medibles, transparentes y construidas para escalar." },
        { n: "02", t: "Visión", d: "Un ecosistema de marketing de resultados donde los objetivos del anunciante y los incentivos del publisher estén siempre alineados." },
        { n: "03", t: "Valores", d: "Transparencia, resultados medibles y alianzas de largo plazo por encima de las victorias de corto plazo." },
        { n: "04", t: "Cómo trabajamos", d: "Estructuramos cada campaña en torno a métricas de éxito claras y optimizamos continuamente con datos de conversión reales." },
        { n: "05", t: "Por qué existe WANM", d: "Porque el crecimiento debe ganarse con resultados, no prometerse con métricas de vanidad." },
      ],
    },
    insights: {
      heroOver: "Ideas",
      heroTitle: "Perspectivas sobre resultados.",
      heroSub: "Ideas sobre marketing de afiliación, estrategia de resultados y el negocio del crecimiento medible.",
      read: "Leer artículo",
      empty: "Aún no hay artículos en esta categoría.",
      back: "Todas las Ideas",
    },
    contact: {
      heroOver: "Contacto",
      heroTitle: "Hablemos de resultados.",
      brandPath: "Soy una Marca",
      brandPathBody: "Escala la adquisición con nuestro ecosistema de publishers.",
      pubPath: "Soy un Publisher",
      pubPathBody: "Monetiza tu tráfico con ofertas de calidad.",
      formOver: "Consulta general",
      formTitle: "Envíanos un mensaje.",
    },
    form: {
      name: "Nombre completo", company: "Empresa", email: "Email de trabajo", website: "Sitio web",
      industry: "Industria", budget: "Presupuesto mensual de marketing", objective: "Objetivo principal de adquisición",
      message: "Mensaje", country: "País", traffic_sources: "Fuentes de tráfico",
      verticals: "Verticales principales", monthly_traffic: "Tráfico mensual", networks: "Redes de afiliación actuales",
      submit: "Enviar", sending: "Enviando…", success: "Gracias, nos pondremos en contacto pronto.",
      error: "Algo salió mal. Inténtalo de nuevo.", required: "Este campo es obligatorio.",
    },
    footer: {
      desc: "WANM conecta marcas y publishers mediante alianzas basadas en resultados. Marketing de Resultados & Red de Afiliación.",
      nav: "Navegar", legal: "Legal", connect: "Conectar",
      privacy: "Política de Privacidad", terms: "Términos y Condiciones", cookie: "Política de Cookies",
      rights: "Todos los derechos reservados.", big: "Construyamos resultados.",
      placeholder: "Datos de la empresa próximamente",
      cookiePreferences: "Preferencias de Cookies", comingSoon: "Próximamente",
      contact: "Contacto",
    },
    cookies: {
      title: "Tu privacidad, tu elección",
      body: "WANM utiliza cookies y tecnologías similares para el funcionamiento esencial del sitio y, con tu consentimiento, para analítica y futuros fines de marketing. Puedes aceptar todo, rechazar las no esenciales o elegir qué permitir.",
      acceptAll: "Aceptar Todo", rejectAll: "Rechazar Todo", manage: "Gestionar Preferencias",
      save: "Guardar Preferencias", cancel: "Cancelar",
      prefTitle: "Preferencias de Cookies",
      prefIntro: "Gestiona cómo WANM utiliza las cookies. Las estrictamente necesarias están siempre activas. Puedes cambiar estas opciones en cualquier momento desde el pie de página.",
      always: "Siempre activas", policyLink: "Lee nuestra Política de Cookies",
      privacyLink: "Política de Privacidad",
    },
    admin: { title: "WANM Admin", login: "Iniciar sesión", leads: "Leads", articles: "Artículos",
      logout: "Cerrar sesión", newArticle: "Nuevo Artículo", noLeads: "Aún no hay envíos." },
  },
};

const categories = ["All", "Affiliate Marketing", "Performance Marketing", "Publisher Strategy",
  "Customer Acquisition", "Digital Advertising", "Industry Insights"];

const LangContext = createContext(null);

export function LangProvider({ children }) {
  const [lang, setLang] = useState(localStorage.getItem("wanm_lang") || "en");
  const toggle = useCallback(() => {
    setLang((p) => {
      const next = p === "en" ? "es" : "en";
      localStorage.setItem("wanm_lang", next);
      return next;
    });
  }, []);
  return (
    <LangContext.Provider value={{ lang, setLang, toggle, t: dict[lang], categories }}>
      {children}
    </LangContext.Provider>
  );
}

export const useLang = () => useContext(LangContext);
export { dict, categories };
